import React from 'react';
import styled from '@emotion/styled';
import { Info, CheckCircle2, AlertTriangle, XCircle, X } from 'lucide-react';

/**
 * BottomNotice — Kuutio's notification: one line at the bottom of the screen,
 * a primary action in Sitra yellow, a quiet secondary, and a way to close it.
 *
 * It says something happened and offers a next step; it never blocks. The
 * tone colours the left edge and the icon and nothing else, so four tones
 * still read as one element. Pure presentation: what to say and what the
 * buttons do come from the parent.
 *
 * Positioned absolutely by default, inside whatever the parent makes
 * `position: relative` (the map canvas); `fixed` for page-level use.
 */

const TONE = {
  info: { color: 'var(--color-primary)', Icon: Info },
  success: { color: 'var(--color-success)', Icon: CheckCircle2 },
  warning: { color: 'var(--color-warning)', Icon: AlertTriangle },
  error: { color: 'var(--color-error)', Icon: XCircle },
};

const Wrap = styled.div`
  position: ${(p) => p.$position};
  left: 50%;
  bottom: 22px;
  transform: translateX(-50%);
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 12px;
  width: max-content;
  max-width: min(640px, calc(100vw - 32px));
  padding: 10px 12px 10px 14px;
  border-radius: 12px;
  background: var(--surface-elevated);
  border: 1px solid var(--surface-border);
  border-left: 4px solid ${(p) => p.$color};
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.5);
  color: var(--text-on-dark);
  font-family: var(--font-sans);
  font-size: var(--fs-body-s);
  line-height: 1.5;
`;

const IconWrap = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  color: ${(p) => p.$color};
`;

const Message = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

const Primary = styled.button`
  all: unset;
  cursor: pointer;
  flex: 0 0 auto;
  padding: 7px 14px;
  border-radius: 8px;
  background: var(--color-accent);
  color: #000;
  font-size: var(--fs-body-s);
  font-weight: var(--fw-bold);
  line-height: 1.1;
  white-space: nowrap;
  &:hover { filter: brightness(1.06); }
  &:focus-visible { outline: 2px solid var(--color-focus-outline); outline-offset: 2px; }
`;

const Secondary = styled.button`
  all: unset;
  cursor: pointer;
  flex: 0 0 auto;
  padding: 7px 10px;
  border-radius: 8px;
  color: var(--text-muted-on-dark);
  font-size: var(--fs-body-s);
  font-weight: var(--fw-semibold);
  white-space: nowrap;
  &:hover { color: var(--text-on-dark); }
  &:focus-visible { outline: 2px solid var(--color-focus-outline); outline-offset: 2px; }
`;

const Dismiss = styled.button`
  all: unset;
  cursor: pointer;
  flex: 0 0 auto;
  display: inline-flex;
  padding: 4px;
  border-radius: 6px;
  color: var(--text-muted-on-dark);
  &:hover { color: var(--text-on-dark); }
  &:focus-visible { outline: 2px solid var(--color-focus-outline); outline-offset: 2px; }
`;

export default function BottomNotice({
  message,
  tone = 'info',
  icon = null,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  onDismiss,
  position = 'absolute',
  dismissLabel = 'Sulje',
}) {
  const { color, Icon } = TONE[tone] || TONE.info;
  return (
    <Wrap role="status" aria-live="polite" $color={color} $position={position}>
      <IconWrap $color={color}>{icon || <Icon size={18} />}</IconWrap>
      <Message>{message}</Message>
      {primaryLabel && <Primary type="button" onClick={onPrimary}>{primaryLabel}</Primary>}
      {secondaryLabel && <Secondary type="button" onClick={onSecondary}>{secondaryLabel}</Secondary>}
      {onDismiss && !secondaryLabel && (
        <Dismiss type="button" onClick={onDismiss} aria-label={dismissLabel}><X size={16} /></Dismiss>
      )}
    </Wrap>
  );
}
